import { createStore, produce } from "solid-js/store";
import type { AiJobOfferPayload } from "../../types";
import type { RefineState, RefineStepId } from "../types";
import { fromAiProposal } from "../utils/offer-subcategories";

function mapPayloadToState(
  payload: Partial<AiJobOfferPayload>,
): Omit<RefineState, "currentStep" | "completedSteps"> {
  return {
    title: payload.title ?? "",
    job_category: payload.job_category ?? null,
    subcategories: fromAiProposal(payload.job_subcategory),
    description: payload.description ?? "",
    remote_policy: payload.remote_policy ?? null,
    payment_type: payload.payment_type ?? null,
    skills: payload.skills ?? [],
    experience_level: payload.experience_level ?? null,
    duration: payload.duration ?? null,
    effort: payload.effort ?? null,
    start_date: payload.start_date ?? null,
    budget: payload.budget ?? null,
    daily_rate_min: payload.daily_rate_min ?? null,
    daily_rate_max: payload.daily_rate_max ?? null,
    currency: payload.currency ?? "EUR",
    location: payload.location ?? null,
    renewable: payload.renewable ?? false,
    // ATS defaults
    internal_ats: true,
    apply_url: "",
    calendar_url: "",
  };
}

export function createRefineStore(payload: Partial<AiJobOfferPayload>) {
  const [state, setState] = createStore<RefineState>({
    currentStep: 1,
    completedSteps: [],
    ...mapPayloadToState(payload),
  });

  function goToStep(step: RefineStepId) {
    setState("currentStep", step);
  }

  function completeStep(step: RefineStepId) {
    setState(
      produce((draft) => {
        if (!draft.completedSteps.includes(step)) {
          draft.completedSteps.push(step);
        }
      }),
    );
  }

  function nextStep() {
    const current = state.currentStep;
    completeStep(current);
    if (current < 5) {
      goToStep((current + 1) as RefineStepId);
    }
  }

  function prevStep() {
    const current = state.currentStep;
    if (current > 1) {
      goToStep((current - 1) as RefineStepId);
    }
  }

  function updateFields(fields: Partial<RefineState>) {
    setState(
      produce((draft) => {
        Object.assign(draft, fields);
      }),
    );
  }

  return {
    get state() {
      return state;
    },
    goToStep,
    completeStep,
    nextStep,
    prevStep,
    updateFields,
  };
}

export type RefineStoreValue = ReturnType<typeof createRefineStore>;
